import React, { useContext, useEffect, useRef, useState } from "react";
import { AuthService } from "src/auth/auth-service";
import { FixedFeeConfigSearchBar } from "./fixed-fee-config-search-bar";
import {
  Alert,
  Grid,
  SpaceBetween,
  Spinner,
} from "@amzn/awsui-components-console";
import "src/index.scss";
import { Domain, RegionalDomain } from "src/config/BartLambdaConfig";
import { FullPageTitle } from "src/components/header/title";
import { inputValidation } from "src/commons/input-validation";
import { Auth } from "aws-amplify";
import {
  DealStatus,
  FuseAccountingCentralDatastore,
  GetFixedFeeConfigCommand,
  UpdateFixedFeeStatusCommand,
  ValidateFixedFeeConfigCommand,
} from "@amzn/fuse-accounting-central-datastore-client";
import { getFacdConfig } from "src/config/fuse-accounting-central-datastore-config";
import {
  putAuditHistory,
  updateFixedFeeStatusAdditionalInfo,
} from "src/commons/audit-history-helper";
import { InitDomain } from "src/context/domain/domainContext";
import { FixedFeeConfigDisplay } from "src/components/fixedFeeConfigPage/fixed-fee-config-display";
import { PartnerPortalLink } from "src/components/fixedFeeConfigPage/partner-portal-link";
import { ValidateAction } from "src/components/fixedFeeConfigPage/operational/validate-action";
import { EnableAction } from "src/components/fixedFeeConfigPage/operational/enable-action";
import { Notifications, useNotifications } from "src/pages/commons";
import {
  facdErrorBody,
  failureContent,
  renderValidationErrorList,
} from "src/components/fixedFeeConfigPage/operational/failure-content";

const VALIDATION_FAILED_HEADING =
  "Configuration validation failed with following errors:";

export const AppFixedFeeConfig: React.FC = () => {
  const [authenticating, setAuthenticating] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [payload, setPayload] = useState<any>("init");
  const [ldap, setLdap] = useState("");
  const [fixedFeeId, setFixedFeeId] = useState(
    window.location.pathname.split("/")[2] || "",
  );
  const [invalidInputs, setInvalidInputs] = useState(false);

  const { notifications, notify } = useNotifications();

  const notifyFailure = (heading: React.ReactNode, body: React.ReactNode) =>
    notify({ type: "error", content: failureContent(heading, body) });

  const authObj = useRef(new AuthService());
  const domainData = useContext(InitDomain);
  const facdClient = useRef<any>();

  useEffect(() => {
    const fetchData = async () => {
      // Run authentication
      await authObj.current.authenticate();
      // set loading state to false after authentication completes. This will cause the page to re-render.
      setAuthenticating(false);
      setIsLoggedIn(authObj.current.getIsLoggedIn());
      setLdap(authObj.current.ldap);

      // Load FixedFeeConfig data by default on component mount
      if (authObj.current.getIsLoggedIn()) {
        setPayload("init");
        await getFacdClient(domainData.domain);

        if (fixedFeeId) {
          await getFixedFeeConfig();
        }
      }
    };
    fetchData();
  }, [domainData.domain]);

  const getFacdClient = async (domain: Domain) => {
    const credentials = await Auth.currentCredentials();
    const essentialCredentials = Auth.essentialCredentials(credentials); // Extract essential credentials

    facdClient.current = new FuseAccountingCentralDatastore({
      ...getFacdConfig(domain), // Get the configuration for the domain
      credentials: essentialCredentials, // Pass essential credentials
    });
  };

  const getFixedFeeConfig = async () => {
    try {
      const getFixedFeeConfigCommand = new GetFixedFeeConfigCommand({
        fixedFeeId,
      });

      console.log(
        "Starting FACD getFixedFeeConfig operation with GetFixedFeeCommand:",
        getFixedFeeConfigCommand,
      );

      const response = await facdClient.current.send(getFixedFeeConfigCommand);
      console.log("Response received from facdClient:", response);
      setPayload(response);
    } catch (error: any) {
      console.error("An error occurred in getFixedFeeConfig:", error);
      setPayload(error.message);
    } finally {
      console.log("Get FixedFeeConfig operation completed.");
    }
  };

  const validateFixedFeeConfig = async () => {
    try {
      const validateFixedFeeConfigCommand = new ValidateFixedFeeConfigCommand({
        fixedFeeConfig: payload.fixedFeeConfig,
      });

      console.log(
        "Starting FACD validateFixedFeeConfig operation with ValidateFixedFeeConfigCommand:",
        validateFixedFeeConfigCommand,
      );

      const response = await facdClient.current.send(
        validateFixedFeeConfigCommand,
      );
      console.log("Response received from facdClient:", response);
      if (response.result) {
        notify({
          type: "success",
          content:
            "Configuration has been validated successfully with no errors.",
        });
      } else {
        notifyFailure(
          VALIDATION_FAILED_HEADING,
          renderValidationErrorList(response.validationErrors),
        );
      }
    } catch (error: any) {
      console.error("An error occurred in validateFixedFeeConfig:", error);
      notifyFailure(VALIDATION_FAILED_HEADING, error.message);
    } finally {
      console.log("Validate FixedFeeConfig operation completed.");
    }
  };

  const updateFixedFeeStatus = async (
    status: DealStatus,
    withValidation: boolean,
    approvalReason: string,
  ) => {
    try {
      const updateFixedFeeStatusCommand = new UpdateFixedFeeStatusCommand({
        fixedFeeId: payload.fixedFeeConfig.fixedFeeId,
        dealStatus: status,
        withValidation,
        changeDescription: `FixedFeeConfig status update to ${status} from FuseBartPortal: ${approvalReason}`,
      });

      console.log(
        "Starting FACD updateFixedFeeStatus operation with UpdateFixedFeeStatusCommand:",
        updateFixedFeeStatusCommand,
      );

      const response = await facdClient.current.send(
        updateFixedFeeStatusCommand,
      );
      console.log("Response received from facdClient:", response);
      // Reuse the returned config to refresh the displayed payload and the
      // action buttons (e.g. Enable disables once no longer in DRAFT).
      setPayload(response);
      putAuditHistory(domainData.domain as RegionalDomain, {
        userId: authObj.current.userId,
        operationType: "FixedFee Config Status Update",
        additionalInfo: updateFixedFeeStatusAdditionalInfo(
          payload.fixedFeeConfig.fixedFeeId,
          status,
          withValidation,
        ),
        reasoning: approvalReason,
      });
      notify({
        type: "success",
        content: `Configuration status updated to ${status} successfully.`,
      });
      return response.fixedFeeConfig;
    } catch (error: any) {
      console.error("An error occurred in updateFixedFeeStatus:", error);
      notifyFailure(
        `Configuration status failed to be updated to ${status}:`,
        facdErrorBody(error),
      );
    } finally {
      console.log("Update FixedFeeStatus operation completed.");
    }
  };

  const search = async () => {
    setPayload("loading");
    if (inputValidation(fixedFeeId)) {
      setInvalidInputs(true);
    } else {
      setInvalidInputs(false);
      await getFixedFeeConfig();
    }
  };

  return (
    <div>
      <FullPageTitle
        title={"Get FixedFee Config Info"}
        description={"Check FixedFee Config information based on FixedFee ID"}
      />
      <div style={{ paddingLeft: "10px", paddingRight: "10px" }}>
        <SpaceBetween size="l">
          {!(ldap.includes("bart-read-only") || ldap.includes("bart-admin")) ? (
            <Grid gridDefinition={[{ colspan: { l: 7, xxs: 10 } }]}>
              <Alert
                header="Only authorized users can access this page!"
                type="error"
              ></Alert>
            </Grid>
          ) : (
            <>
              <Notifications items={notifications} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <FixedFeeConfigSearchBar
                  keyword={fixedFeeId}
                  setValue={(fixedFeeId: string) => setFixedFeeId(fixedFeeId)}
                  onClickSearch={search}
                />
                {payload?.fixedFeeConfig && (
                  <SpaceBetween
                    direction="horizontal"
                    size="xs"
                    alignItems="center"
                  >
                    <ValidateAction
                      fixedFeeConfig={payload.fixedFeeConfig}
                      ldap={ldap}
                      onValidate={validateFixedFeeConfig}
                    />
                    <EnableAction
                      fixedFeeConfig={payload.fixedFeeConfig}
                      ldap={ldap}
                      onEnable={({ withEnhancedValidation, reason }) =>
                        updateFixedFeeStatus(
                          DealStatus.ENABLED,
                          withEnhancedValidation,
                          reason,
                        )
                      }
                    />
                    <PartnerPortalLink
                      fixedFeeConfig={payload.fixedFeeConfig}
                    />
                  </SpaceBetween>
                )}
              </div>
              {invalidInputs ? (
                <div>
                  <Grid gridDefinition={[{ colspan: { l: 7, xxs: 10 } }]}>
                    <Alert
                      header="Invalid input! FixedFee ID field cannot be empty"
                      type="error"
                    ></Alert>
                  </Grid>
                </div>
              ) : null}
              {(() => {
                switch (payload) {
                  case "init":
                    return <></>;
                  case "loading":
                    return <Spinner size="large"></Spinner>;
                  default:
                    if (payload.fixedFeeConfig) {
                      return (
                        <FixedFeeConfigDisplay
                          fixedFeeConfig={payload.fixedFeeConfig}
                          domain={domainData.domain as RegionalDomain}
                        />
                      );
                    } else {
                      return (
                        <Grid gridDefinition={[{ colspan: { l: 7, xxs: 10 } }]}>
                          <Alert header={payload} type="error"></Alert>
                        </Grid>
                      );
                    }
                }
              })()}
            </>
          )}
        </SpaceBetween>
      </div>
    </div>
  );
};

export default AppFixedFeeConfig;
